// Translation utility functions for consistent data mapping across all portals

export const createNameMapper = (nameMap: Record<string, string>) => (name: string) => nameMap[name] || name;

// Accommodation name mapping
export const getAccommodationName = createNameMapper({
  'Le Renard': 'chalets.leRenard',
  'Le Lynx': 'chalets.leLynx',
  'La Perdrix': 'chalets.laPerdrix',
  'Le Loup': 'chalets.leLoup',
  'Le Huard': 'chalets.leHuard',
  'La Bécasse': 'chalets.laBecasse',
  'L\'Original': 'chalets.lOriginal',
  'Yourte': 'accommodations.yourtes'
});

// Activity name mapping
export const getActivityName = createNameMapper({
  'Pêche au doré': 'activities.fishingWalleye',
  'Kayak': 'activities.kayak',
  'Randonnée guidée': 'activities.guidedHiking',
  'Chasse au chevreuil': 'activities.deerHunting',
  'Ski de fond': 'activities.crossCountrySkiing',
  'Pêche sur glace': 'activities.iceFishing'
});

// Package name mapping
export const getPackageName = createNameMapper({
  'Forfait Pêche Familiale': 'packages.familyFishing',
  'Weekend Romantique': 'packages.romanticWeekend',
  'Aventure Hivernale': 'packages.winterAdventure'
});

// Description mapping
export const getAccommodationDescription = createNameMapper({
  'Le Renard': 'chalets.leRenard.desc',
  'Le Lynx': 'chalets.leLynx.desc',
  'La Perdrix': 'chalets.laPerdrix.desc',
  'Le Loup': 'chalets.leLoup.desc',
  'Le Huard': 'chalets.leHuard.desc',
  'La Bécasse': 'chalets.laBecasse.desc',
  'L\'Original': 'chalets.lOriginal.desc'
});

export const getActivityDescription = createNameMapper({
  'Pêche au doré': 'activities.fishingWalleye.desc',
  'Kayak': 'activities.kayak.desc',
  'Randonnée guidée': 'activities.guidedHiking.desc',
  'Chasse au chevreuil': 'activities.deerHunting.desc',
  'Ski de fond': 'activities.crossCountrySkiing.desc',
  'Pêche sur glace': 'activities.iceFishing.desc'
});

export const getPackageDescription = createNameMapper({
  'Forfait Pêche Familiale': 'packages.familyFishing.desc',
  'Weekend Romantique': 'packages.romanticWeekend.desc',
  'Aventure Hivernale': 'packages.winterAdventure.desc'
});

// Location mapping
export const getLocationLabel = (location: string, t: (key: string) => string) => {
  const locationMap: Record<string, string> = {
    'lac': 'common.lakefront',
    'forêt': 'common.forest',
    'mixte': 'common.mixed'
  };
  return t(locationMap[location] || location);
};

// Type mapping
export const getAccommodationTypeLabel = (type: string, t: (key: string) => string) => {
  const typeMap: Record<string, string> = {
    'chalet': 'manager.accommodations.chalet',
    'yourte': 'manager.accommodations.yourte',
    'unite_rangée': 'manager.accommodations.units'
  };
  return t(typeMap[type] || type);
};

export const getClientAccommodationTypeLabel = (type: string, t: (key: string) => string) => {
  const typeMap: Record<string, string> = {
    'chalet': 'accommodations.chalets',
    'yourte': 'accommodations.yourtes',
    'unite_rangée': 'accommodations.units'
  };
  return t(typeMap[type] || type);
};

// Amenity mapping
export const getAmenityLabel = (amenity: string, t: (key: string) => string) => {
  const amenityMap: Record<string, string> = {
    'foyer': 'common.fireplace',
    'quai_privé': 'common.privateDock',
    'quai_partagé': 'common.sharedDock',
    'cuisine_équipée': 'common.equippedKitchen',
    'cuisine_commune': 'common.commonKitchen',
    'terrasse': 'common.terrace',
    'barbecue': 'common.barbecue',
    'salle_jeux': 'common.gameRoom'
  };
  return t(amenityMap[amenity] || amenity.replace('_', ' '));
};

// Duration mapping
export const getActivityDuration = (duration: string, t: (key: string) => string) => {
  const match = duration.match(/(\d+)\s*(\w+)/);
  if (match) {
    const number = match[1];
    const unit = match[2];
    
    const unitMap: Record<string, string> = {
      'heures': 'common.hours',
      'journée': 'common.day'
    };
    
    return `${number}${t(unitMap[unit] || unit)}`;
  }
  return duration;
};

// Activity type mapping
export const getActivityType = (type: string, t: (key: string) => string) => {
  const typeMap: Record<string, string> = {
    'été': 'common.summer',
    'hiver': 'common.winter',
    'automne': 'common.autumn',
    'toute_année': 'common.yearRound'
  };
  return t(typeMap[type] || type);
};

// Status mapping
export const getStatusColor = (status: string) => {
  const colorMap: Record<string, string> = {
    'confirmed': 'bg-green-100 text-green-800',
    'pending': 'bg-yellow-100 text-yellow-800',
    'cancelled': 'bg-red-100 text-red-800',
    'available': 'bg-green-100 text-green-800',
    'occupied': 'bg-red-100 text-red-800',
    'maintenance': 'bg-yellow-100 text-yellow-800'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800';
};

// Priority mapping
export const getPriorityColor = (priority: string) => {
  const colorMap: Record<string, string> = {
    'high': 'bg-red-100 text-red-800',
    'urgent': 'bg-red-100 text-red-800',
    'medium': 'bg-yellow-100 text-yellow-800',
    'low': 'bg-blue-100 text-blue-800'
  };
  return colorMap[priority] || 'bg-gray-100 text-gray-800';
};


